import { eventRegistration } from '../utils/eventRegistration';
import { sumRupees } from '../utils/inr';
// client/src/components/EventEnrollmentReport.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Resolve API base at runtime (works for localhost and production)
const API_BASE = (typeof window !== 'undefined' && window.__APP_CONFIG__ && window.__APP_CONFIG__.API_URL)
  || process.env.REACT_APP_API_URL
  || axios.defaults.baseURL
  || 'http://localhost:5050';
axios.defaults.baseURL = API_BASE;

const isMongoId = (v) => typeof v === 'string' && /^[a-f\d]{24}$/i.test(v);

function fmtINR(n) { return Number(n || 0).toLocaleString("en-IN"); }
function toYMD(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? new Date(date.getTime() + 330 * 60000).toISOString().slice(0,10) : '';
}

function resolveCourseName(courseField, courseMap) {
  // If populated object with name
  if (courseField && typeof courseField === 'object' && courseField.name) {
    return courseField.name;
  }
  // If it's a probable ObjectId string, try map lookup
  if (isMongoId(courseField) && courseMap) {
    const c = courseMap.get(String(courseField));
    if (c && c.name) return c.name;
  }
  // Fallback: don't show the raw ID; show placeholder
  return '_';
}

export default function EventEnrollmentReport() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(() => new URLSearchParams(window.location.search).get("eventId") || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [courses, setCourses] = useState([]);
  const [restoreStudent, setRestoreStudent] = useState('');
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState('');
  const [cancellation, setCancellation] = useState(null);
  const [details, setDetails] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const guard = useRef(false);
  const labels = { unknown: 'Unknown / Unverified', registered: 'Registered', attended: 'Attended', didNotAttend: 'Did Not Attend', cancelled: 'Cancelled' };
  async function writeParticipation(id, status, notes = '') {
    if (guard.current) return;
    guard.current = true; setSaving(id || 'register'); setErr('');
    try {
      if (!id) {
        const row = rows.find(row => row.studentId === restoreStudent && row.registration === 'cancelled' && row.hasPayment && !row.ambiguous);
        if (!row?.id) throw new Error('Select a cancelled, paid registration for this event.');
        await axios.patch(`/api/event-participation/${row.id}/status`, { status: 'registered', expectedVersion: row.version });
      }
      else {
        const row = rows.find(row => row.id === id || row.studentId === id);
        if (!row || row.ambiguous) throw new Error('This historical relationship needs review before changes.');
        let relationshipId = row.id, version = row.version;
        if (!relationshipId) {
          const created = await axios.post('/api/event-participation', { studentId: row.studentId, eventId });
          relationshipId = created.data._id; version = created.data.__v || 0;
        }
        await axios.patch(`/api/event-participation/${relationshipId}/status`, { status, expectedVersion: version, ...(status === 'cancelled' ? { confirmCancellation: true, notes } : {}) });
      }
      setRevision(value => value + 1); setRestoreStudent(''); setCancellation(null); setDetails(null);
    } catch (error) { setCancellation(null); setErr(error.response?.data?.error || 'Unable to update participation.'); setRevision(value => value + 1); }
    finally { guard.current = false; setSaving(''); }
  }

  // load events (active first, but include all)
  useEffect(() => {
    let keep = true;
    (async () => {
      try {
        const res = await axios.get("/api/events");
        const list = Array.isArray(res.data) ? res.data : [];
        // Active first, then by startDate desc
        list.sort((a, b) => {
          const act = (b.isActive === true) - (a.isActive === true);
          if (act !== 0) return act;
          const bd = b.startDate ? new Date(b.startDate) : 0;
          const ad = a.startDate ? new Date(a.startDate) : 0;
          return bd - ad; // newest first
        });
        if (keep) setEvents(list);
      } catch (e) {
        console.error("Failed to load events", e);
      }
    })();
    return () => { keep = false; };
  }, []);

  useEffect(() => {
    let keep = true;
    (async () => {
      try {
        const res = await axios.get('/api/courses');
        const list = Array.isArray(res.data) ? res.data : [];
        if (keep) setCourses(list);
      } catch (e) {
        console.error('Failed to load courses', e);
      }
    })();
    return () => { keep = false; };
  }, []);

  const courseMap = useMemo(() => {
    const m = new Map();
    for (const c of courses) {
      if (c && c._id) m.set(String(c._id), c);
    }
    return m;
  }, [courses]);

  // load participants when event changes
  useEffect(() => {
    if (!eventId) { setRows([]); return; }
    let keep = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        // Your backend route: GET /api/event-participation/event/:eventId
        const res = await axios.get(`/api/event-participation/event/${eventId}`);
        const data = Array.isArray(res.data) ? res.data : [];
        // Normalize rows for table
        const mapped = data.map((p) => {
          const s = p.student || {};
          const ev = p.event || {};
          const pays = Array.isArray(p.payments) ? p.payments : [];
          const registration = p.registrationStatus || eventRegistration(p);
          const status = p.participationStatus || 'unknown';
          return {
            id: p._id,
            ambiguous: p.ambiguousStatus || (p.relationshipIds || []).length > 1,
            studentId: s._id,
            version: p.__v || 0,
            status: status === 'unknown' && registration === 'registered' ? 'registered' : status,
            attendance: p.attendanceStatus || (['cancelled','didNotAttend'].includes(status) ? status : registration === 'registered' ? 'attended' : status === 'attended' ? 'attended' : 'unknown'),
            registration,
            registrationVerified: registration === 'registered',
            unallocated: p.unallocatedPaymentCount || 0,
            hasPayment: pays.some(payment => !payment.voided && (Number(payment.amount) > 0 || Number(payment.grossAmount) > 0)) ||
              (!pays.some(payment => payment.source === 'receipt') && Number(p.legacyRecordedAmount ?? p.amountPaid) > 0),
            studentName: s.name || '-',
            studentCode: s.studentId || '',
            phone: s.phone || '',
            whatsapp: s.whatsapp || '',
            courseName: resolveCourseName(s.course, courseMap),
            eventName: ev.name || '-',
            startDate: ev.startDate ? toYMD(ev.startDate) : '',
            endDate: ev.endDate ? toYMD(ev.endDate) : '',
            amountPaid: Number(p.amountPaid || 0),
            payments: pays.map(x => ({
              amount: x?.amount == null ? null : Number(x.amount),
              grossAmount: x?.grossAmount,
              paymentMode: x?.paymentMode || 'Unknown',
              receiptReference: String(x?.receiptReference || x?.payment?._id || x?.payment || 'Unavailable'),
              voided: x?.voided,
              originalAmount: x?.originalAmount,
              voidReason: x?.voidReason,
              note: (x?.note || '').trim(),
              date: x?.date || null,
            })),
            createdAt: p.createdAt ? toYMD(p.createdAt) : '',
            updatedAt: p.updatedAt ? toYMD(p.updatedAt) : '',
          };
        });
        if (keep) setRows(mapped);
      } catch (e) {
        console.error("Failed to load participants", e);
        setErr("Failed to load participants");
      } finally {
        if (keep) setLoading(false);
      }
    })();
    return () => { keep = false; };
  }, [eventId, courseMap, revision]);

  const eventObj = useMemo(() => events.find(e => e._id === eventId), [events, eventId]);
  const restorable = rows.filter(row => row.registration === 'cancelled' && row.hasPayment && !row.ambiguous && row.id);
  const noteSummary = row => row.payments.map(payment => {
    if (payment.voided) return `Removed - ${fmtINR(payment.originalAmount)}: ${payment.voidReason}`;
    const amount = payment.grossAmount ?? payment.amount;
    return `${payment.note || 'Event Fee'}${amount == null ? ' - amount unverified' : ` - ${fmtINR(amount)}`}`;
  }).join('; ') || '—';
  const twoLines = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal', overflowWrap: 'anywhere' };
  const totalCollected = useMemo(() => sumRupees(rows.map(row => row.amountPaid || 0), true), [rows]);

  const exportCSV = () => {
    const header = [
      "Event",
      "Student",
      "Student Code",
      "Phone",
      "WhatsApp",
      "Course",
      "Amount Paid (INR)",
      "Notes", "Registration Status", "Attendance Status", "Receipt Count", "Unallocated / Unverified",
    ];
    const body = rows.map(r => {
      const payNote = (Array.isArray(r.payments) ? r.payments : [])
        .map(x => {
          const label = x.voided ? `Removed as correction (original INR ${fmtINR(x.originalAmount)}): ${x.voidReason}` : (x.note && x.note.trim()) ? x.note.trim() : '—';
          const amt = Number(x.amount || 0);
          return `${label}; ${x.date ? toYMD(x.date) : 'Unknown date'}; ${x.paymentMode}; ${x.receiptReference}; ${x.amount == null ? 'Unverified allocation' : `INR ${amt}`}`;
        })
        .join(' | ');
      return [
        r.eventName,
        r.studentName,
        r.studentCode,
        r.phone,
        r.whatsapp,
        r.courseName,
        Number(r.amountPaid || 0),
        payNote, r.registration === 'registered' ? 'Registered' : r.registration === 'cancelled' ? 'Cancelled' : 'Needs review', labels[r.attendance] || 'Unverified', r.payments.length, r.unallocated,
      ];
    });
    const csv = [header, ...body].map(line =>
      line.map(v => {
        const s = String(v ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = (s) => (s || "").replace(/[^a-z0-9-_]+/gi, "_");
    a.href = url;
    a.download = `event_enrollment_${safe(eventObj?.name || "event")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pad = 40;

    const orgTitle = "Event Enrollment Report";
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(orgTitle, pad, 40);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(`Event: ${eventObj?.name || "-"}`, pad, 62);
    if (eventObj?.startDate || eventObj?.endDate) {
      doc.text(
        `Dates: ${toYMD(eventObj?.startDate)} - ${toYMD(eventObj?.endDate)}`,
        pad,
        80
      );
    }
    if (typeof eventObj?.eventFee === "number")
      doc.text(`Event Fee: INR ${fmtINR(eventObj.eventFee)}`, pad, 98);

    const head = [['Student', 'Code', 'Contact', 'Course', 'Notes', 'Amount (INR)', 'Registration', 'Payments']];
    const body = rows.map(row => [row.studentName, row.studentCode,
      [...new Set([row.phone, row.whatsapp].filter(Boolean))].join(' / '),
      row.courseName.replace(/\s+/g, ' '), noteSummary(row), fmtINR(row.amountPaid),
      row.registration === 'registered' ? 'Registered' : row.registration === 'cancelled' ? 'Cancelled' : 'Needs review',
      String(row.payments.length)]);

    autoTable(doc, {
      head,
      body,
      startY: 120,
      styles: { fontSize: 8, cellPadding: 3, overflow: 'ellipsize', halign: 'left', valign: 'middle' },
      headStyles: { fillColor: [67, 56, 202], textColor: 255 },
    });

    const y = doc.lastAutoTable.finalY + 18;
    doc.setFont("helvetica", "bold");
    doc.text(`Verified Collected: INR ${fmtINR(totalCollected)}`, pad, y);

    const safe = (s) => (s || "").replace(/[^a-z0-9-_]+/gi, "_");
    doc.save(`event_enrollment_${safe(eventObj?.name || "event")}.pdf`);
  };

  return (
    <div className="max-w-screen-2xl mx-auto p-4">
      <h2 className="text-2xl font-semibold mb-4">Event Enrollment Report</h2>

      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="reportEvent" className="block text-sm text-gray-600 mb-1">Select Event</label>
          <select id="reportEvent"
            className="border rounded px-3 py-2 min-w-[260px]"
            value={eventId}
            onChange={e => { setRows([]); setErr(''); setCancellation(null); setDetails(null); setRestoreStudent(''); setEventId(e.target.value); }}
          >
            <option value="">— Choose an event —</option>
            {events.length === 0 && (
              <option value="" disabled>(no events found)</option>
            )}
            {events.map(ev => (
              <option key={ev._id} value={ev._id}>
                {(ev.isActive ? '🟢' : '⚪')} {ev.name}
              </option>
            ))}
          </select>
        </div>

        <div className="ml-auto flex gap-2">
          <button
            type="button"
            disabled={!eventId || loading || rows.length === 0}
            onClick={exportCSV}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Export CSV
          </button>
          <button
            type="button"
            disabled={!eventId || loading || rows.length === 0}
            onClick={exportPDF}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            Export PDF
          </button>
        </div>
      </div>

      {eventId && <div className="bg-white rounded border p-3 mb-4 flex flex-wrap gap-3 items-end">
        <label>Restore Registration<select aria-label="Restore Registration" className="block border rounded p-2" value={restoreStudent} onChange={event => setRestoreStudent(event.target.value)} disabled={loading || !restorable.length}>
          <option value="">{restorable.length ? 'Choose cancelled, paid student' : 'No cancelled paid registrations'}</option>
          {restorable.map(row => <option key={row.studentId} value={row.studentId}>{row.studentName} · {row.studentCode} · {row.phone || ''}</option>)}
        </select></label>
        <button type="button" disabled={!restorable.some(row => row.studentId === restoreStudent) || Boolean(saving) || loading} onClick={() => writeParticipation()} className="bg-indigo-600 text-white rounded px-3 py-2">Restore</button>
      </div>}
      {eventId && <p className="text-sm mb-3">Registered: {rows.filter(row => row.registration === 'registered').length} · Attended: {rows.filter(row => row.attendance === 'attended').length} · Cancelled: {rows.filter(row => row.registration === 'cancelled').length} · Payment entries: {rows.reduce((total,row) => total + row.payments.filter(payment => !payment.voided).length, 0)}</p>}
      {cancellation && <div role="dialog" aria-modal="true" aria-label="Confirm participation cancellation" className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"><div className="bg-white rounded p-5 w-full max-w-lg space-y-3">
        <h3 className="font-semibold">Cancel participation?</h3><p>{cancellation.studentName} ({cancellation.studentCode}) · {cancellation.eventName}</p><p>Payments will remain unchanged. This does not issue a refund.</p>
        <label>Cancellation reason<input aria-label="Cancellation reason" maxLength={500} value={cancelReason} onChange={event => setCancelReason(event.target.value)} className="block border rounded p-2 w-full" /></label>
        <button type="button" disabled={Boolean(saving)} onClick={() => setCancellation(null)} className="border rounded p-2 mr-2">Keep participating</button>
        <button type="button" disabled={!cancelReason.trim() || Boolean(saving)} onClick={() => writeParticipation(cancellation.id || cancellation.studentId, 'cancelled', cancelReason.trim())} className="bg-red-700 text-white rounded p-2">Confirm Cancellation</button>
      </div></div>}
      {details && <div role="dialog" aria-modal="true" aria-label="Event participation details" className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4"><div className="bg-white rounded p-5 w-full max-w-4xl max-h-[90vh] overflow-y-auto space-y-3">
        <div className="flex justify-between gap-3"><h3 className="font-semibold">{details.studentName} · {details.studentCode}</h3><button type="button" className="border rounded px-3 py-1" onClick={() => setDetails(null)}>Close details</button></div>
        <p>{details.eventName} · {details.startDate || 'Event date unavailable'}</p>
        <p>Registration: {details.registration === 'registered' ? 'Registered' : details.registration === 'cancelled' ? 'Cancelled' : 'Needs review'}. Registration counts as attendance. Cancelled and Did Not Attend events are excluded.</p>
        {details.studentId && !details.ambiguous && <label>Participation / Attendance<select aria-label={`Participation for ${details.studentCode}`} value={details.status} disabled={Boolean(saving)} onChange={event => writeParticipation(details.id || details.studentId, event.target.value)} className="block border rounded p-2">{Object.entries(labels).filter(([value]) => value !== 'cancelled' || details.status === 'cancelled').map(([value,label]) => <option key={value} value={value} disabled={value === 'unknown' || value === 'cancelled'}>{label}</option>)}</select></label>}
        <p>Confirmed collections: INR {fmtINR(details.amountPaid)}{details.unallocated > 0 ? ` · ${details.unallocated} unverified allocation(s), excluded from this total.` : ''}</p>
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr><th>Date</th><th>Amount</th><th>Mode</th><th>Receipt</th><th>Notes</th></tr></thead><tbody>{details.payments.map((payment,index) => <tr key={index} className="border-t"><td className="p-2 whitespace-nowrap">{payment.date ? toYMD(payment.date) : 'Unknown date'}</td><td className="p-2 whitespace-nowrap">{payment.voided ? `Removed (original INR ${fmtINR(payment.originalAmount)})` : payment.amount == null ? `Unverified (gross INR ${fmtINR(payment.grossAmount)})` : `INR ${fmtINR(payment.amount)}`}</td><td className="p-2">{payment.paymentMode}</td><td className="p-2 break-all">{payment.receiptReference}</td><td className="p-2">{payment.voided ? payment.voidReason : payment.note || '—'}</td></tr>)}</tbody></table></div>
        {!details.payments.length && <p>No event payment entries.</p>}
      </div></div>}
      {loading && <div>Loading…</div>}
      {err && <div className="text-red-600 mb-4">{err}</div>}

      {!loading && !err && eventId && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b font-medium flex justify-between items-center">
            <div>
              {eventObj?.name || "-"}{" "}
              <span className="text-sm text-gray-500">
                ({rows.length} participants)
              </span>
            </div>
            <div className="font-medium">
              Verified collections: INR {fmtINR(Number(totalCollected || 0))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap" aria-label="Event registrations">
              <thead className="bg-gray-50"><tr>
                <th className="text-left px-3 py-2">Student</th>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-left px-3 py-2">Contact</th>
                <th className="text-left px-3 py-2">Course</th>
                <th className="text-left px-3 py-2 w-[28%] min-w-[220px]">Notes</th>
                <th className="text-right px-3 py-2">Amount (INR)</th>
                <th className="text-left px-3 py-2">Registration</th>
                <th className="text-left px-3 py-2">Details</th>
              </tr></thead>
              <tbody>{rows.map(r => {
                const contacts = [...new Set([r.phone, r.whatsapp].filter(Boolean))].join(' / ');
                return <tr key={r.id || r.studentId} className="border-t">
                  <td className="px-3 py-2"><span className="block max-w-[200px] truncate" title={r.studentName}>{r.studentName}</span></td>
                  <td className="px-3 py-2">{r.studentCode}</td>
                  <td className="px-3 py-2" title={contacts}>{contacts || '—'}</td>
                  <td className="px-3 py-2 min-w-[170px] max-w-[220px]"><span style={twoLines} title={r.courseName}>{r.courseName}</span></td>
                  <td className="px-3 py-2 min-w-[220px] max-w-[420px]"><span style={twoLines} title={noteSummary(r)}>{noteSummary(r)}</span></td>
                  <td className="px-3 py-2 text-right">INR {fmtINR(r.amountPaid)}{r.unallocated > 0 && <span title="Some payment allocations remain unverified"> *</span>}</td>
                  <td className="px-3 py-2"><div className="flex items-center gap-2">
                    <span aria-label={r.registration === 'registered' ? 'Registered' : r.registration === 'cancelled' ? 'Cancelled' : 'Needs review'} title={r.registration === 'registered' ? 'Registered' : r.registration === 'cancelled' ? 'Cancelled' : 'Needs review'} className={`inline-flex items-center gap-1 ${r.registration === 'cancelled' ? 'text-red-700' : r.registration === 'registered' ? 'text-green-700' : 'text-amber-700'}`}><span aria-hidden="true" className={`inline-block h-2 w-2 rounded-full ${r.registration === 'registered' ? 'bg-green-600' : r.registration === 'cancelled' ? 'bg-red-600' : 'bg-amber-500'}`} />{r.registration === 'registered' ? 'R' : r.registration === 'cancelled' ? 'C' : '?'}</span>
                    {r.studentId && !r.ambiguous && r.registration !== 'cancelled' && <button type="button" disabled={Boolean(saving)} className="text-red-700 underline" onClick={() => { setCancellation(r); setCancelReason(''); }}>Cancel</button>}
                  </div></td>
                  <td className="px-3 py-2"><button type="button" className="text-indigo-700 underline" onClick={() => setDetails(r)}>Details</button></td>
                </tr>;
              })}{rows.length === 0 && <tr><td colSpan={8} className="px-3 py-4 text-center">No participants for this event.</td></tr>}</tbody>
              {rows.length > 0 && <tfoot><tr className="border-t bg-gray-50"><td colSpan={5} className="px-3 py-2">Aggregate</td><td className="px-3 py-2 text-right font-semibold">INR {fmtINR(totalCollected)}</td><td colSpan={2} /></tr></tfoot>}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}